import { useEffect, useRef, useCallback } from "react";
import { createLogger } from "../../services/Debug";

export const AUTO_CHUNK_INTERVAL_MS = 2 * 60 * 1000;
export const AUTO_CHUNK_MIN_DURATION_MS = 2000;

export function useAutoChunk({
  enabled,
  isRecording,
  isPaused,
  stopRecording,
  startRecording,
  addToQueue,
  processQueue,
  processingRef,
  recordingDuration,
  isNative,
  nativeDurationRef,
}) {
  const log = useRef(createLogger("useAutoChunk")).current;

  const timeoutRef = useRef(null);
  const chunkingRef = useRef(false);
  const prevIsRecordingRef = useRef(false);
  const prevIsPausedRef = useRef(false);

  const depsRef = useRef({});
  depsRef.current = {
    enabled,
    stopRecording,
    startRecording,
    addToQueue,
    processQueue,
    processingRef,
    recordingDuration,
    isNative,
    nativeDurationRef,
  };

  const clear = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const runChunkCycle = useCallback(async () => {
    const d = depsRef.current;
    const currentMs =
      d.isNative && d.nativeDurationRef
        ? d.nativeDurationRef.current
        : d.recordingDuration;

    if (currentMs < AUTO_CHUNK_MIN_DURATION_MS) {
      return true;
    }

    let blob = null;
    try {
      blob = await d.stopRecording();
    } catch (e) {
      log.error("stopRecording failed during auto-chunk:", e);
      return false;
    }

    if (blob && blob.size > 0) {
      const durationMs =
        d.isNative && d.nativeDurationRef
          ? d.nativeDurationRef.current
          : currentMs;
      const durationSeconds =
        durationMs > 0 ? Math.round(durationMs / 1000) : undefined;
      try {
        d.addToQueue(
          blob,
          Date.now().toString(),
          durationSeconds ? { durationSeconds } : {}
        );
        setTimeout(() => {
          if (!d.processingRef?.current) {
            d.processQueue();
          }
        }, 100);
      } catch (e) {
        log.error("addToQueue failed during auto-chunk:", e);
      }
    } else {
      log.warn("auto-chunk produced empty blob, skipping queue");
    }

    try {
      const ok = await d.startRecording();
      if (!ok) {
        log.error("startRecording returned false after auto-chunk stop");
        return false;
      }
    } catch (e) {
      log.error("startRecording failed after auto-chunk stop:", e);
      return false;
    }

    return true;
  }, [log]);

  const scheduleNext = useCallback(() => {
    clear();
    timeoutRef.current = setTimeout(async () => {
      timeoutRef.current = null;
      if (chunkingRef.current) return;
      chunkingRef.current = true;
      try {
        const reschedule = await runChunkCycle();
        if (reschedule && depsRef.current.enabled) {
          scheduleNext();
        }
      } finally {
        chunkingRef.current = false;
      }
    }, AUTO_CHUNK_INTERVAL_MS);
  }, [clear, runChunkCycle]);

  useEffect(() => {
    const wasRecording = prevIsRecordingRef.current;
    const wasPaused = prevIsPausedRef.current;
    prevIsRecordingRef.current = isRecording;
    prevIsPausedRef.current = isPaused;

    if (chunkingRef.current) return;

    if (!enabled) {
      clear();
      return;
    }

    const nowActive = isRecording && !isPaused;
    const wasActive = wasRecording && !wasPaused;

    if (nowActive && !wasActive) {
      scheduleNext();
    } else if (!nowActive && wasActive) {
      clear();
    }
  }, [enabled, isRecording, isPaused, scheduleNext, clear]);

  useEffect(() => () => clear(), [clear]);
}
